import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, Dimensions, ScrollView, useWindowDimensions, useColorScheme, Platform, TouchableOpacity } from "react-native";
import { showMessage } from "react-native-flash-message";
import { useTheme } from '@react-navigation/native';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import CustomText from '../../../components/CustomText/CustomText';
import { ActivityIndicator } from 'react-native-paper';
import { useSignin } from '../../../hooks/useSignIn';
import { useSettings } from '../../../hooks/useSettings';
import AsyncStorage from '@react-native-async-storage/async-storage';

const window = Dimensions.get("window");
const screen = Dimensions.get("screen");

import { Light, Dark } from '../../../img/logos';
import { useTranslation } from 'react-i18next';

const SignInScreen = ({ navigation, route }) => {
  const { t } = useTranslation();

  let colorScheme = useColorScheme();
  const { colors } = useTheme();

  const styles = makeStyles(colors);

  const usernameRef = useRef(null);
  const passwordRef = useRef(null);
  const submitClickedRef = useRef(null);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitClicked, setSubmitClicked] = useState(false);
  const [dimensions, setDimensions] = useState({ window, screen });

  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const { isWinAuth } = useSettings();
  const { signin, isLoading, versionMismatch, setVersionMismatch } = useSignin();

  useEffect(() => {
    usernameRef.current = username;
    if (submitClickedRef.current) {
      validateUsername();
    }
  }, [username]);

  useEffect(() => {
    passwordRef.current = password;
    if (submitClickedRef.current) {
      validatePassword();
    }
  }, [password]);

  useEffect(() => {
    submitClickedRef.current = submitClicked;
  }, [submitClicked]);

  const onSignInPressed = async (e) => {
    e.preventDefault();
    setSubmitClicked(true);

    if (!validateUsername() | !validatePassword()) {
      outputErrorMessage(t('common:errAllFields'));
    } else {
      await AsyncStorage.getItem('client_api').then((response) => {
        if (response !== undefined && response !== null) {
          let rJson = JSON.parse(response);
          isWinAuth(rJson.clientApi).then((resp) => {
            signin(usernameRef.current, passwordRef.current, rJson.clientApi);
          });
        }
        else {
          console.error(t('common:apiNotExists'));
        }
      });
    }
  };

  const outputErrorMessage = (message) => {
    showMessage({
      message: message,
      type: "danger",
      duration: 3000,
    });
  };

  const validateUsername = () => {
    if (usernameRef.current === "") {
      setUsernameError(t('common:errRequired'));
      return false;
    } else {
      setUsernameError("");
      return true;
    }
  };

  const validatePassword = () => {
    if (passwordRef.current === "") {
      setPasswordError(t('common:errRequired'));
      return false;
    } else {
      setPasswordError("");
      return true;
    }
  };

  const { height } = useWindowDimensions();

  return (
    <ScrollView
      style={styles.areaview}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}
    >
      <View style={styles.root}>
        {versionMismatch && (
          <View style={styles.versionPopup}>
            <View style={styles.versionPopupHeader}>
              <CustomText style={styles.versionPopupTitle}>{t("common:versionMismatchTitle")}</CustomText>
              <TouchableOpacity onPress={() => setVersionMismatch(null)}>
                <CustomText style={styles.versionPopupClose}>✕</CustomText>
              </TouchableOpacity>
            </View>

            <CustomText style={styles.versionPopupMessage}>
              {versionMismatch.message}
            </CustomText>

{/* 
            {versionMismatch.backendVersion && (
              <View style={styles.versionRow}>
                <CustomText style={styles.versionLabel}>Backend:</CustomText>
                <CustomText style={styles.versionValue}>{versionMismatch.backendVersion}</CustomText>
              </View>
            )}

            {versionMismatch.frontendVersion && (
              <View style={styles.versionRow}>
                <CustomText style={styles.versionLabel}>Frontend:</CustomText>
                <CustomText style={styles.versionValue}>{versionMismatch.frontendVersion}</CustomText>
              </View>
            )}

            {versionMismatch.databaseVersion && (
              <View style={styles.versionRow}>
                <CustomText style={styles.versionLabel}>Database:</CustomText>
                <CustomText style={styles.versionValue}>{versionMismatch.databaseVersion}</CustomText>
              </View>
            )}

*/}
          </View>
        )}

        <Light />

        <CustomInput
          placeholder={t('common:username')}
          value={username}
          setValue={setUsername}
          type="LOGIN"
          autoCapitalize='none'
        />
        <CustomText style={styles.error}>{usernameError}</CustomText>

        <CustomInput
          placeholder={t('common:password')}
          value={password}
          setValue={setPassword}
          submitEdit={onSignInPressed}
          type="LOGIN"
          autoCapitalize='none'
          secureTextEntry
        />
        <CustomText style={styles.error}>{passwordError}</CustomText>

        <CustomButton
          text={t('common:signIn')}
          disabled={isLoading}
          onPress={onSignInPressed}
          type="LOGIN"
          isGradient={true}
        />

        <CustomText style={styles.copyright}>Oasys by Deloitte</CustomText>

        {isLoading &&
          <View style={styles.loading}>
            <ActivityIndicator animating={true} size='large' color="#86BC25" />
          </View>
        }
      </View>
    </ScrollView>
  );
};

const makeStyles = (colors) => StyleSheet.create({
  areaview: {
    backgroundColor: '00F0F8FF'
  },
  root: {
    flex: 1,
    alignItems: "center",
    justifyContent: 'center',
    width: Platform.OS === 'web' ? (window.width < 500 ? window.width : window.width * 0.2) : window.width * 0.9,
    padding: 20,
    backgroundColor: colors.background,
  },
  versionPopup: {
    position: 'absolute',
    top: 20,
    alignSelf: 'center',
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#FFF8E8',
    borderWidth: 1,
    borderColor: '#F0C36D',
    borderRadius: 14,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 10,
    elevation: 6,
    zIndex: 999,
  },
  versionPopupHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  versionPopupTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#8A5A00',
  },
  versionPopupClose: {
    fontSize: 18,
    color: '#8A5A00',
    fontWeight: 'bold',
  },
  versionPopupMessage: {
    marginTop: 8,
    marginBottom: 12,
    fontSize: 14,
    color: '#5F4300',
  },
  versionRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  versionLabel: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#6B4A00',
  },
  versionValue: {
    fontSize: 13,
    color: '#6B4A00',
  },
  title_hidden: {
    paddingTop: '10%',
    paddingBottom: '10%',
  },
  title: {
    fontSize: 24,
    paddingTop: '20%',
    paddingBottom: '10%',
    fontWeight: 'bold',
    color: colors.text_input,
    margin: 10,
  },
  tinyLogo: {
    width: 152,
    height: 172,
  },
  error: {
    color: colors.validation_error,
    alignSelf: 'center',
    fontSize: 14,
  },
  copyright: {
    marginVertical: '10%',
    paddingBottom: '12%',
    fontSize: 13,
    color: '#6e6e6e',
  }
});

export default SignInScreen;